// ─────────────────────────────────────────────
//  TimelineCaseModal — click a case bar on the Timeline Chart to act on the
//  underlying assignment without leaving the page:
//    • Edit the case START / END times (writes the same assignment row the OR
//      Board reads, so the board updates live).
//    • Relieve the CRNA and/or the supervising doctor: optionally swap in a
//      replacement from today's roster, and dispose the relieved person to
//      Home (marked departed), Unassigned (freed to the bench), or — CRNAs
//      only — Travel to another facility (creates a transfer; they appear in
//      the 🚗 Traveling box on the board + TV as "→ destination").
//    • Replace or remove the SECOND CRNA when two people are in the room.
//
//  ⚠️ TWO PEOPLE READ AS TWO PEOPLE. A legacy row's `ahpName` is still the
//  mashed sheet cell — "Trevor Stamand 5 orient Kayla Gibbons" — and this modal
//  printed it as one person's name three times over (the slot header, the
//  Replace dropdown, and the "Relieved … →" line). That is the screenshot the
//  product owner sent on 2026-08-18. `domain/secondProvider` splits it; nothing
//  in this file parses a name.
//
//  ⚠️ AND EVERY CONTROL NAMES ITS PERSON. With two CRNAs in the room, one
//  "Relieve CRNA" button is one verb for two actions — the exact failure this
//  repo already carries a scar for. Each button says who it moves.
//
//  Presentational shell over the shared firestore helpers; the page passes the
//  roster pools + ids. Only rendered for users who can manage assignments.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import Modal from "./Modal";
import Button from "./Button";
import PersonSlot from "./timelineCase/PersonSlot";
import RoomPeople from "./timelineCase/RoomPeople";
import SecondCrnaSection from "./timelineCase/SecondCrnaSection";
import AssignSeatSection from "./timelineCase/AssignSeatSection";
import CaseFactsSection from "./timelineCase/CaseFactsSection";
import st from "./timelineCase/caseModalStyles";
import { useToast } from "../context/ToastContext";
import { setRosterMemberFreeSince, updateAssignmentRow, setRoomProvider, setRosterMemberDeparted, createTransfer } from "../firebase/firestore";
import { otherFacilities, facilityLabel } from "../domain/facilities";
import { stageReliefPatch } from "../domain/relief";
import { secondProviderOf } from "../domain/secondProvider";
import {
  seatCellName,
  setSecondProviderPatch,
  clearSecondProviderPatch,
  asAssignmentRow,
} from "../domain/secondProviderWrite";

export default function TimelineCaseModal({ caseItem, crnaPool = [], doctorPool = [], facilityId, date, uid, surgeons = [], onClose }) {
  const toast = useToast();
  const c = caseItem;
  const [startTime, setStartTime] = useState(c?.startTime || "");
  const [endTime, setEndTime] = useState(c?.endTime || "");
  const [surgeon, setSurgeon] = useState(c?.surgeon || "");
  const [crnaReplId, setCrnaReplId] = useState("");
  const [crnaDispo, setCrnaDispo] = useState("unassigned");
  const [crnaTravelDest, setCrnaTravelDest] = useState("");
  const [mdReplId, setMdReplId] = useState("");
  const [mdDispo, setMdDispo] = useState("unassigned");
  const [secondReplId, setSecondReplId] = useState("");
  // Filling an EMPTY seat is a different action from relieving an occupied one:
  // nobody is being sent anywhere, so there is no disposition to choose.
  const [addMdId, setAddMdId] = useState("");
  const [addCrnaId, setAddCrnaId] = useState("");
  const [busy, setBusy] = useState(false);

  if (!c) return null;

  // ── WHO IS ACTUALLY IN THIS ROOM ─────────────────────────────────────────
  // One resolver, asked once. `seat` is the person HOLDING the room; `second`
  // is the CRNA standing with them, or null.
  // `asAssignmentRow` only reshapes — a timeline case publishes the second
  // person on `case.orientee`, and `secondProviderOf` reads assignment-row
  // fields. Nothing here parses a name.
  const row = asAssignmentRow(c);
  const second = secondProviderOf(row);
  const seat = seatCellName(c);

  // Replacement pickers exclude everybody already in this room — offering the
  // second CRNA as a replacement for the seat would double-book her into the
  // room she is standing in.
  const inRoomIds = new Set([c.ahpId, second?.id].filter(Boolean));
  const crnaOptions = crnaPool.filter((p) => !inRoomIds.has(p.id));
  const mdOptions = doctorPool.filter((p) => p.id !== c.attendingId);
  // Travel destinations = the other campuses (Tampa / Carrollwood / WC).
  const travelFacilities = otherFacilities(facilityId);

  const dispose = async (oldId, dispo, { name = "", subRole = "crna", travelDest = "" } = {}) => {
    if (!oldId) return;
    if (dispo === "home") await setRosterMemberDeparted(facilityId, date, oldId, true, uid);
    if (dispo === "travel") {
      // Same shape as the board's travel flows (SoloReliefFlow/BenchAssign):
      // a transfer doc puts them in the 🚗 Traveling box on BOTH facilities'
      // boards + TVs ("→ destination"); departed keeps them off the bench
      // until Arrival/Cancel resolves the trip.
      await createTransfer({
        providerId: oldId,
        providerName: name,
        subRole,
        fromFacilityId: facilityId,
        toFacilityId: travelDest,
        toFacilityName: facilityLabel(travelDest),
        date,
        createdBy: uid,
      });
      await setRosterMemberDeparted(facilityId, date, oldId, true, uid);
    }
    // "unassigned" needs nothing extra: clearing the slot frees them to the bench.
  };

  // Only the surgeon moves. RoomCard reads `asg.surgeon` straight off the row
  // with no room-doc fallback (RoomCard.js:866), so unlike a staffing change
  // there is no second document to keep in step here.
  const saveSurgeon = async () => {
    setBusy(true);
    try {
      await updateAssignmentRow(c.id, { surgeon });
      toast.success(
        surgeon ? `${c.roomName} surgeon set to ${surgeon}.` : `${c.roomName} surgeon cleared.`
      );
      onClose();
    } catch (e) {
      console.error("Timeline save surgeon failed:", e);
      toast.error("Could not update the surgeon.");
    } finally { setBusy(false); }
  };

  const saveTimes = async () => {
    setBusy(true);
    try {
      await updateAssignmentRow(c.id, { startTime, endTime });
      toast.success(`${c.roomName} times updated.`);
      onClose();
    } catch (e) {
      console.error("Timeline save times failed:", e);
      toast.error("Could not update the times.");
    } finally { setBusy(false); }
  };

  // ── STAGE A RELIEF (pending), rather than swapping the seat now ──────────
  //
  // ⚠️ TWO DIFFERENT ACTIONS WORE ONE ⇄ GLYPH. This modal's Replace path writes
  // `ahpId`/`attendingId` STRAIGHT AWAY and disposes the outgoing person — the
  // handover is over before anyone is told. The OR Board's relief writes
  // `crnaReliefId`/`mdReliefId` instead, which is what puts "Relief X — pending"
  // and the ✓ Take over button on the room card, and what the incoming provider
  // confirms on their phone. Starting from the timeline produced no pending
  // state at all, reported live: "from the board works perfectly, but when you
  // start from the timeline it doesn't show this in the board".
  //
  // It calls the SAME `stageReliefPatch` the board calls, so the two surfaces
  // cannot drift about what "staged" means.
  //
  // ⚠️ THE SEAT ONLY. There is no pending slot for a second provider, so
  // SecondCrnaSection deliberately offers no staged path — see the note there.
  const stageRelief = async (kind) => {
    const isMd = kind === "md";
    const repl = isMd
      ? doctorPool.find((p) => p.id === mdReplId)
      : crnaPool.find((p) => p.id === crnaReplId);
    if (!repl) return;
    setBusy(true);
    try {
      await updateAssignmentRow(
        c.id,
        stageReliefPatch({
          assignment: c,
          crna: isMd ? undefined : repl,
          md: isMd ? repl : undefined,
        })
      );
      // No room-doc mirror here, deliberately: nobody has changed seats yet. The
      // room is still covered by the person in it, and writing the reliever onto
      // the room card would say the handover already happened.
      toast.success(
        `${repl.name} is staged to relieve ${isMd ? (c.attendingName || "the doctor") : (seat || "the CRNA")} in ${c.roomName}. They confirm the takeover.`
      );
      onClose();
    } catch (e) {
      console.error("Timeline stage relief failed:", e);
      toast.error("Could not stage the relief.");
    } finally { setBusy(false); }
  };

  // `solo` — the CRNA leaves and the SUPERVISING DOCTOR keeps the room, running
  // it themselves. Owner, 2026-09-07: "add a button that the doctor stay in the
  // room solo and the crna is reasigned."
  //
  // ⚠️ IT IS THE SAME WRITE AS A RELIEF, with the doctor as the incoming person.
  // A solo case in this codebase IS `providerId === attendingId`
  // (domain/caseRole.isSoloCase), so putting the attending in the seat is not a
  // special state to invent — it is the state the board, the ratio maths and the
  // Room Status screen already understand. Inventing a `soloMd: true` flag
  // instead would be a second way to say the same thing, and the surfaces that
  // did not learn it would show the room as unstaffed.
  const applyCrna = async ({ solo = false } = {}) => {
    if (crnaDispo === "travel" && !crnaTravelDest) {
      toast.error("Pick the facility they are traveling to.");
      return;
    }
    if (solo && !c.attendingId) {
      // Refusing out loud beats a button that writes an empty seat.
      toast.error("This room has no supervising doctor to run it.");
      return;
    }
    const repl = solo
      ? { id: c.attendingId, name: c.attendingName || "the doctor", shiftCode: null }
      : crnaPool.find((p) => p.id === crnaReplId) || null;
    setBusy(true);
    try {
      const patch = repl
        ? { ahpId: repl.id, ahpName: repl.name, ahpShiftCode: repl.shiftCode ?? null, ahpDepartTime: "" }
        : { ahpId: null, ahpName: "", ahpShiftCode: null, ahpDepartTime: "" };
      await updateAssignmentRow(c.id, patch);
      // The CRNA who just came OUT of the room is now free. Stamp when, so
      // FREE NOW can count rather than guess. Only when nobody replaced them and
      // we actually know who left — a missing stamp renders as TBD, which is the
      // honest fallback, so this must never stamp speculatively.
      // ⚠️ `ahpId`, not `crnaId` — the latter does not exist on a timeline case
      // (buildTimeline emits ahpId and crnaKey, and crnaKey falls back to a NAME
      // when there is no id). Guessing the field would have made this condition
      // permanently false: no stamp, no timer, every provider reading TBD
      // forever, with nothing on screen or in the console to say why.
      // `ahpId` is also exactly what benchProviders matches on, so the key the
      // stamp is written under is the key the bench looks it up by.
      if (!repl && c.ahpId && facilityId && date) {
        try {
          await setRosterMemberFreeSince(facilityId, date, c.ahpId, uid);
        } catch (err) {
          // A failed stamp must not fail the relieve itself. The room change is
          // the clinical act; the timer is reporting. Degrades to TBD.
          console.error("freeSince stamp failed:", err);
        }
      }
      // ⚠️ The row and the room doc must move together. RoomCard resolves
      // `asg.ahpName || room.providerName`, so clearing the row alone leaves the
      // card showing the relieved CRNA — a room that reads as staffed and is
      // not. Status is deliberately untouched: a running room stays running.
      if (c.roomId) {
        await setRoomProvider(c.roomId, repl
          ? { providerId: repl.id, providerName: repl.name }
          : { providerId: null, providerName: "" });
      }
      const relieved = crnaPool.find((p) => p.id === c.ahpId);
      await dispose(c.ahpId, crnaDispo, {
        name: seat || relieved?.name || "",
        subRole: relieved?.subRole || "crna",
        travelDest: crnaTravelDest,
      });
      const destText =
        crnaDispo === "home" ? "home"
        : crnaDispo === "travel" ? `traveling to ${facilityLabel(crnaTravelDest)}`
        : "unassigned";
      toast.success(
        solo ? `${repl.name} is running ${c.roomName} solo; ${seat} → ${destText}.`
        : repl ? `${repl.name} now covers ${c.roomName}; ${seat} → ${destText}.`
             : `${seat} relieved from ${c.roomName} → ${destText}.`
      );
      onClose();
    } catch (e) {
      console.error("Timeline relieve CRNA failed:", e);
      toast.error("Could not relieve the CRNA.");
    } finally { setBusy(false); }
  };

  const applyMd = async () => {
    const repl = doctorPool.find((p) => p.id === mdReplId) || null;
    setBusy(true);
    try {
      const patch = repl
        ? { attendingId: repl.id, attendingName: repl.name }
        : { attendingId: null, attendingName: "" };
      await updateAssignmentRow(c.id, patch);
      // Same two-write rule as the CRNA path above.
      if (c.roomId) {
        await setRoomProvider(c.roomId, repl
          ? { supervisingDoctorId: repl.id, supervisorName: repl.name }
          : { supervisingDoctorId: null, supervisorName: "" });
      }
      await dispose(c.attendingId, mdDispo);
      toast.success(
        repl ? `${repl.name} now supervises ${c.roomName}; ${c.attendingName} → ${mdDispo === "home" ? "home" : "unassigned"}.`
             : `${c.attendingName} relieved from ${c.roomName} → ${mdDispo === "home" ? "home" : "unassigned"}.`
      );
      onClose();
    } catch (e) {
      console.error("Timeline relieve doctor failed:", e);
      toast.error("Could not relieve the doctor.");
    } finally { setBusy(false); }
  };

  // ── THE SECOND CRNA ──────────────────────────────────────────────────────
  // ⚠️ NO ROOM-DOC MIRROR, unlike every seat write above. `room.providerId` /
  // `providerName` is ONE field pair describing who holds the room, and the
  // second provider does not — writing them here would tell RoomCard that Kayla
  // is running WOR 9 and Trevor has gone.
  const writeSecond = async (patch, message, failure) => {
    setBusy(true);
    try {
      await updateAssignmentRow(c.id, patch);
      toast.success(message);
      onClose();
    } catch (e) {
      console.error("Timeline second-CRNA write failed:", e);
      toast.error(failure);
    } finally { setBusy(false); }
  };

  const replaceSecond = async () => {
    const repl = crnaPool.find((p) => p.id === secondReplId);
    if (!repl || !second) return;
    await writeSecond(
      setSecondProviderPatch(row, repl),
      `${repl.name} is now the second CRNA in ${c.roomName}; ${second.name} is out of the room.`,
      "Could not replace the second CRNA."
    );
  };

  const removeSecond = async () => {
    if (!second) return;
    await writeSecond(
      clearSecondProviderPatch(row),
      `${second.name} is out of ${c.roomName}. ${seat || "The CRNA"} still has the room.`,
      "Could not remove the second CRNA."
    );
  };

  // ── FILLING AN EMPTY SEAT ─────────────────────────────────────────────────
  // Both sections below used to render ONLY when somebody was already in the
  // seat, so a room with no supervising doctor — or no CRNA — offered no control
  // at all: you could relieve a person, never assign one. That is the dead end
  // behind "3 rooms have no supervising MD" and "1 room uncovered" on the board;
  // the warning named the problem and the fix was nowhere.
  //
  // Deliberately NOT the relieve path: nothing is being vacated, so there is no
  // disposition (home / unassigned / travel) to pick, and offering one would ask
  // where to send a person who was never here.
  const assignSeat = async (kind) => {
    const isMd = kind === "md";
    const pick = (isMd ? doctorPool : crnaPool).find((p) => p.id === (isMd ? addMdId : addCrnaId));
    if (!pick) return;
    setBusy(true);
    try {
      await updateAssignmentRow(
        c.id,
        isMd
          ? { attendingId: pick.id, attendingName: pick.name }
          : { ahpId: pick.id, ahpName: pick.name }
      );
      // The same two-write rule the relieve paths follow: the assignment row and
      // the room card must agree, or the board and the room disagree about who
      // is in it.
      if (c.roomId) {
        await setRoomProvider(
          c.roomId,
          isMd
            ? { supervisingDoctorId: pick.id, supervisorName: pick.name }
            : { providerId: pick.id, providerName: pick.name }
        );
      }
      toast.success(
        isMd
          ? `${pick.name} now supervises ${c.roomName}.`
          : `${pick.name} now covers ${c.roomName}.`
      );
      onClose();
    } catch (e) {
      console.error("Timeline assign seat failed:", e);
      toast.error(isMd ? "Could not assign the supervisor." : "Could not assign the CRNA.");
    } finally { setBusy(false); }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${c.roomName} — ${c.start12h}–${c.end12h}`}
      footer={<Button variant="secondary" onClick={onClose} disabled={busy}>Close</Button>}
    >
      <div style={st.body}>
        {/* Surgeon and supervising doctor stated at the top, beside the case
            times, so a second CRNA section cannot push them out of sight. */}
        <RoomPeople
          surgeon={c.surgeon}
          attendingName={c.attendingName}
          seatName={seat}
          secondName={second?.name || ""}
        />

        {/* The case itself — times and surgeon, both writing the board's row. */}
        <CaseFactsSection
          startTime={startTime} setStartTime={setStartTime}
          endTime={endTime} setEndTime={setEndTime}
          savedStartTime={c.startTime} savedEndTime={c.endTime}
          onSaveTimes={saveTimes}
          surgeon={surgeon} setSurgeon={setSurgeon} savedSurgeon={c.surgeon || ""}
          surgeons={surgeons} onSaveSurgeon={saveSurgeon}
          busy={busy}
        />

        {/* Relieve the CRNA who HOLDS the room. */}
        {seat && (
          <div style={st.section}>
            <div style={st.sectionTitle}>
              {second ? "CRNA / AA holding the room" : "Relieve CRNA / AA"}
            </div>
            <PersonSlot kind="CRNA" currentName={seat} pool={crnaOptions}
              replId={crnaReplId} setReplId={setCrnaReplId} disposition={crnaDispo} setDisposition={setCrnaDispo}
              travelFacilities={travelFacilities} travelDest={crnaTravelDest} setTravelDest={setCrnaTravelDest} />
            <div style={st.applyRow}>
              {/* Staged first: it is the ordinary handover, and the one the OR
                  Board offers. Replace is the immediate, irreversible one. */}
              {crnaReplId && (
                <Button onClick={() => stageRelief("crna")} disabled={busy}>
                  {`Stage relief for ${seat}`}
                </Button>
              )}
              <Button variant="secondary" onClick={() => applyCrna()} disabled={busy}>
                {crnaReplId ? `Replace ${seat} now` : `Relieve ${seat}`}
              </Button>
              {/* ⚠️ ITS OWN VERB, NOT A DISPOSITION. "Unassigned / Home / Travel"
                  answer where the CRNA GOES; this answers who is left holding the
                  room, which is a different question with a different
                  consequence — the doctor stops supervising from outside and
                  becomes the person in the room. Two actions must not share one
                  verb, so it gets its own button and names the doctor in it.

                  Offered only when there IS a supervising doctor: a solo button
                  on a room with nobody to be solo writes an empty seat. */}
              {c.attendingId && !crnaReplId && (
                <Button
                  variant="secondary"
                  data-testid="crna-out-md-solo"
                  onClick={() => applyCrna({ solo: true })}
                  disabled={busy}
                  title={`${c.attendingName || "The doctor"} keeps ${c.roomName} and runs it without a CRNA`}
                >
                  {`${c.attendingName || "Doctor"} runs it solo`}
                </Button>
              )}
            </div>
            {crnaReplId && (
              <p style={st.stageNote}>
                Stage relief puts “pending” on the room card and asks them to confirm
                the takeover. Replace now swaps the room immediately.
              </p>
            )}
          </div>
        )}

        {/* The second CRNA — ONLY when one is actually in the room. */}
        <SecondCrnaSection
          roomName={c.roomName}
          seatName={seat}
          secondName={second?.name || ""}
          pool={crnaOptions}
          replId={secondReplId}
          setReplId={setSecondReplId}
          onReplace={replaceSecond}
          onRemove={removeSecond}
          busy={busy}
        />

        {/* NO CRNA IN THE ROOM — assign one. The room is uncovered, which the
            board is already alarming about; this is where that alarm gets its
            answer. */}
        {!seat && (
          <AssignSeatSection
            title="Assign CRNA / AA"
            emptyLabel="Nobody is in this room"
            pickerLabel={`Assign a CRNA to ${c.roomName}`}
            placeholder="— pick a CRNA / AA —"
            pool={crnaPool}
            value={addCrnaId}
            onChange={setAddCrnaId}
            onAssign={() => assignSeat("crna")}
            action="Assign CRNA"
            busy={busy}
          />
        )}

        {/* Relieve supervising doctor. */}
        {c.attendingName && (
          <div style={st.section}>
            <div style={st.sectionTitle}>Relieve supervising doctor</div>
            <PersonSlot kind="MD / DO" currentName={c.attendingName} pool={mdOptions}
              replId={mdReplId} setReplId={setMdReplId} disposition={mdDispo} setDisposition={setMdDispo} />
            <div style={st.applyRow}>
              {mdReplId && (
                <Button onClick={() => stageRelief("md")} disabled={busy}>
                  {`Stage relief for ${c.attendingName}`}
                </Button>
              )}
              <Button variant="secondary" onClick={applyMd} disabled={busy}>
                {mdReplId ? `Replace ${c.attendingName} now` : `Relieve ${c.attendingName}`}
              </Button>
            </div>
            {mdReplId && (
              <p style={st.stageNote}>
                Stage relief puts “pending” on the room card and asks them to confirm
                the takeover. Replace now swaps the room immediately.
              </p>
            )}
          </div>
        )}

        {/* NO SUPERVISING DOCTOR — assign one. Asked for on WC OB, which the
            board was listing under "rooms have no supervising MD" with no way to
            act on it from here. */}
        {!c.attendingName && (
          <AssignSeatSection
            title="Assign supervising doctor"
            emptyLabel="No supervising doctor"
            pickerLabel={`Assign a supervising doctor to ${c.roomName}`}
            placeholder="— pick a doctor —"
            pool={doctorPool}
            value={addMdId}
            onChange={setAddMdId}
            onAssign={() => assignSeat("md")}
            action="Assign supervisor"
            busy={busy}
          />
        )}
      </div>
    </Modal>
  );
}
